import React, { useState, useCallback, SyntheticEvent, useEffect } from 'react';
import { uniq } from 'ramda';
import {
  Clickable,
  Stack,
  Text,
  Input,
  Button,
  Overlay,
  ControlLink,
  RadioButton,
  LoadingBalls
} from '@moda/om';
import { formatPrice, compact } from '@moda/portal-stanchions';
import classNames from 'classnames';
import { reportError } from '../../lib/reportError';
import { useErrorModal } from '../../hooks/useErrorModal';
import { useCookies } from '../../hooks/useCookies';
import { CartPrice } from '../../hooks/useCart';
import { useGiftCode, getInvalidCodeMessage } from './useGiftCode';
import { useAvailableGiftCodes } from './useAvailableGiftCodes';
import './GiftCode.scss';

enum Mode {
  Resting,
  Editing,
  Saving
}

interface Props {
  theme?: 'box' | 'simple';
  promotionDiscount: CartPrice;
}

export const GiftCode: React.FC<Props> = ({ theme = 'box', promotionDiscount }) => {
  const [mode, setMode] = useState(Mode.Resting);
  const [inputGiftCode, setInputGiftCode] = useState('');
  const { showErrorModal } = useErrorModal();
  const { giftCode, invalidCodeMessage, applyGiftCode } = useGiftCode();
  const { data: availableGiftCodesData } = useAvailableGiftCodes();
  const {
    cookies: { promoCode },
    deleteCookie
  } = useCookies();

  const availableCodes = uniq(
    compact([
      ...(availableGiftCodesData?.availableGiftCodes.data || []).map(code => code.attributes.code),
      giftCode === inputGiftCode ? null : giftCode
    ])
  );

  const closeEditView = useCallback(() => {
    setMode(Mode.Resting);
    setInputGiftCode('');
  }, [setMode, setInputGiftCode]);

  const submitCode = useCallback(
    async (submittedCode: string | null) => {
      setMode(Mode.Saving);

      try {
        const code = submittedCode?.toUpperCase();
        const result = await applyGiftCode(code || null);
        if (getInvalidCodeMessage(result.data)) {
          setMode(Mode.Editing);
        } else {
          closeEditView();
        }
      } catch (error) {
        showErrorModal();
        reportError('GIFT_CODE_SUBMIT', error);
      }
    },
    [setMode, applyGiftCode, showErrorModal, closeEditView]
  );

  useEffect(() => {
    if (promoCode) {
      setInputGiftCode(promoCode);
      submitCode(promoCode);
      deleteCookie('promoCode');
    }
  }, [submitCode, deleteCookie, promoCode]);

  useEffect(() => {
    if (invalidCodeMessage && mode === Mode.Resting) {
      setMode(Mode.Editing);
    }
  }, [invalidCodeMessage, mode]);

  const onEdit = useCallback(() => setMode(Mode.Editing), [setMode]);

  const onApply = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault();
      if (inputGiftCode) {
        submitCode(inputGiftCode);
      } else {
        closeEditView();
      }
    },
    [inputGiftCode, submitCode, closeEditView]
  );

  const onCancel = useCallback(
    () => (giftCode ? submitCode(null) : closeEditView()),
    [giftCode, submitCode, closeEditView]
  );

  return (
    <>
      <Stack
        space={0}
        className={classNames('GiftCode', `GiftCode--${theme}`, { 'GiftCode--empty': !giftCode })}
      >
        <Stack
          space={2}
          className="GiftCode__head"
          direction="horizontal"
          justifyContent="space-between"
        >
          <div className="GiftCode__title">Promo Code {giftCode && `Applied`}</div>
          {!giftCode && mode === Mode.Resting && (
            <ControlLink aria-label="Add Gift Code" onClick={onEdit} data-testid="changeGiftCode">
              + Add Code
            </ControlLink>
          )}
          {Number(promotionDiscount.price) > 0 && (
            <div className="GiftCode__content">
              {`-${formatPrice(promotionDiscount.currency, promotionDiscount.price)}`}
            </div>
          )}
        </Stack>

        {giftCode && mode === Mode.Resting && (
          <Stack space={2} direction="horizontal" justifyContent="space-between">
            {mode === Mode.Resting && giftCode && (
              <Text data-testid="appliedGiftCode" treatment="eyebrow" color="cement">
                {giftCode}
              </Text>
            )}
            <ControlLink aria-label="Add Gift Code" onClick={onEdit} data-testid="changeGiftCode">
              Edit Code
            </ControlLink>
          </Stack>
        )}

        {mode !== Mode.Resting && (
          <form onSubmit={onApply}>
            <Stack space={2} className="GiftCode__apply-section">
              <Text treatment="body2" color="cement">
                Limit one gift code per order
              </Text>
              <Stack space={2}>
                {availableCodes.map(code => (
                  <Stack key={code} space={1}>
                    <RadioButton
                      checked={!inputGiftCode && code === giftCode}
                      onChange={() => code !== giftCode && submitCode(code)}
                    >
                      <Text treatment="eyebrow" color="cement">
                        {code}
                      </Text>
                    </RadioButton>

                    {invalidCodeMessage && code === giftCode && inputGiftCode !== giftCode && (
                      <Text className="GiftCode__error" color="code-red">
                        {invalidCodeMessage}
                      </Text>
                    )}
                  </Stack>
                ))}
                <Stack className="GiftCode__row" direction="horizontal" space={2}>
                  {giftCode && <RadioButton readOnly checked={inputGiftCode !== ''} />}
                  <Stack direction="horizontal" space={0}>
                    <Input
                      className="GiftCode__input"
                      placeholder="Enter Promo Code"
                      error={
                        inputGiftCode === giftCode ? invalidCodeMessage || undefined : undefined
                      }
                      value={inputGiftCode}
                      onChange={setInputGiftCode}
                      data-testid="giftCodeInput"
                    />
                    <Button
                      aria-label="Apply Gift Code"
                      type="submit"
                      data-testid="applyGiftCode"
                      className="GiftCode__apply"
                    >
                      Apply
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
              <Stack space={0} direction="horizontal" justifyContent="space-between">
                {!invalidCodeMessage && (
                  <Clickable
                    className="GiftCode__cancel"
                    onClick={onEdit}
                    data-testid="changeGiftCode"
                  >
                    Cancel
                  </Clickable>
                )}
                {giftCode && (
                  <Clickable
                    type="button"
                    className="GiftCode__cancel"
                    onClick={onCancel}
                    data-testid="cancelGiftCode"
                  >
                    {invalidCodeMessage ? 'Cancel' : 'Remove Code'}
                  </Clickable>
                )}
              </Stack>
            </Stack>
          </form>
        )}
      </Stack>

      <Overlay show={mode === Mode.Saving}>
        <LoadingBalls />

        <Text treatment="eyebrow">Saving</Text>
      </Overlay>
    </>
  );
};
